<template>
  <v-layout>
    <v-toolbar dense class="toolbar-leftTop">
      <v-toolbar-title>{{ title }}</v-toolbar-title>

      <v-spacer></v-spacer>
      <instruction-comments
        v-if="Array.isArray(instructionComments)"
        buttonIcon="comment"
        :buttonTitle="$t('instructions.comments.comment')"
        :versionId="versionId"
        :instructionComments="instructionComments"
        @comment-added="$apollo.queries.instructionComments.refetch()"
        :title="title"
      />
      <v-btn
        @click="close"
        small
        class="ma-2"
        color="grey lighten-2"
        style="min-width: 90px"
      >
        <v-icon small left>close</v-icon>
        {{ $t("instructions.close") }}
      </v-btn>

      <test-data
        v-if="(!signed || isRereadable) && isSignable"
        :testId="testId"
        @test-done="testDone = true"
      />
      <v-btn
        v-if="isSignable && canBeSigned"
        @click="sign()"
        small
        class="ma-2"
        color="primary"
        dark
        style="min-width: 90px"
      >
        <v-icon small left dark>check_circle</v-icon>
        {{ $t("instructions.sign.create.action") }}
      </v-btn>
    </v-toolbar>

    <template v-if="extension == 'pdf'">
      <PDFDefaultContent :readingStart.sync="readingStart" />
    </template>

    <template v-else>
      <VideoContent />
    </template>

    <impersonation
      @confirm="sign"
      @cancel="impersonation.show = false"
      :title="$t('instructions.sign.create.action')"
      :text="$t('instructions.sign.create.text')"
      :action="$t('instructions.sign.create.action')"
      :show="impersonation.show"
      :error="impersonation.error"
      :duration="impersonation.duration"
    />

    <test-success-dialog
      :show="testDone"
      :actionText="$t('instructions.sign.create.action')"
      @ok="
        () => {
          testDone = false;
          sign();
        }
      "
      @close="testDone = false"
    />
  </v-layout>
</template>

<script>
import { mapState } from "vuex";

import Impersonation from "@/components/Impersonation";
import VideoContent from "@/components/VideoContent";
import TestData from "@/components/TestData";
import TestSuccessDialog from "@/components/TestSuccessDialog";
import PDFDefaultContent from "@/components/PDFDefaultContent";
import InstructionComments from "@/components/instruction/InstructionComments";

import SignQuery from "@/graphql/Sign.gql";
import RereadingRevokeQuery from "@/graphql/RereadingRevoke.gql";
import AppendReadingDurationQuery from "@/graphql/AppendReadingDuration.gql";
import SetReadingDurationQuery from "@/graphql/SetReadingDuration.gql";
import InstructionCommentsQuery from "@/graphql/queries/instruction/InstructionComments.gql";

export default {
  components: {
    VideoContent,
    PDFDefaultContent,
    Impersonation,
    TestData,
    TestSuccessDialog,
    InstructionComments,
  },

  data() {
    return {
      impersonation: {
        show: false,
        error: false,
        duration: 0,
      },
      readingStart: Date.now(),
      readingDurationSaved: false,
      testDone: false,
    };
  },

  computed: {
    ...mapState(["me"]),
    signed() {
      return (
        this.$route.query.signed === "true" || this.$route.query.signed === true
      );
    },
    isRereadable() {
      return (
        this.$route.query.isRereadable === "true" ||
        this.$route.query.isRereadable === true
      );
    },
    signatureId() {
      return this.$route.query.signatureId;
    },
    confirmationId() {
      return this.$route.query.confirmationId;
    },
    testId() {
      return this.$route.query.testId;
    },
    title() {
      return this.$route.query.title;
    },
    versionId() {
      return this.$route.query.versionId;
    },
    extension() {
      return this.$route.query.url.substring(
        this.$route.query.url.lastIndexOf(".") + 1
      );
    },
    canBeSigned() {
      return !this.testId && (!this.signed || this.isRereadable);
    },
    isSignable() {
      return (
        this.$route.query.isSignable === "true" ||
        this.$route.query.isSignable === true
      );
    },
  },

  methods: {
    rereadingRevoke(signatureId, confirmationId, args) {
      this.impersonation.error = false;
      this.$apollo
        .mutate({
          mutation: RereadingRevokeQuery,
          variables: {
            password: args && args.password,
            confirmationId,
            signatureId,
          },
        })
        .then(() => {
          this.signMutation(args);
        })
        .catch(() => {
          this.impersonation.error = true;
        });
    },

    sign(args) {
      if (!args) {
        this.impersonation = {
          duration: this.getDuration(),
          show: true,
          error: false,
        };
        return;
      } else if (this.signatureId && this.isRereadable) {
        this.rereadingRevoke(this.signatureId, this.confirmationId, args);
        return;
      }
      this.signMutation(args);
    },

    signMutation(args) {
      this.impersonation.error = false;
      this.$apollo
        .mutate({
          mutation: SignQuery,
          variables: {
            fileId: this.$route.params.id,
            password: args && args.password,
          },
        })
        .then(() => {
          this.impersonation.show = false;
          this.close();
        })
        .catch(() => {
          this.impersonation.error = true;
        });
    },

    close() {
      this.readingDuration(
        this.isRereadable ? SetReadingDurationQuery : AppendReadingDurationQuery
      ).then(() => this.$router.push({ name: "profile" }));
    },
    getDuration() {
      return Math.round((Date.now() - this.readingStart) / 1000);
    },
    readingDuration(mutation) {
      if (this.readingDurationSaved) {
        return Promise.resolve();
      }

      const duration = this.getDuration();
      return (
        this.$apollo
          .mutate({
            mutation: mutation,
            variables: {
              employeeId: this.me.id,
              fileId: this.$route.params.id,
              duration,
            },
          })
          // eslint-disable-next-line
          .catch((err) => console.error(err))
          .finally(() => (this.readingDurationSaved = true))
      );
    },
  },
  apollo: {
    instructionComments: {
      query: InstructionCommentsQuery,
      variables() {
        return {
          versionId: this.versionId,
        };
      },
      skip() {
        return !this.versionId;
      },
    },
  },
  destroyed() {
    this.readingDuration(AppendReadingDurationQuery);
  },
};
</script>

<style scoped>
.toolbar-leftTop {
  top: 0;
  width: 100%;
  left: 0;
  position: absolute;
  z-index: 1000;
}
</style>
